import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Menu, Dropdown, Button } from 'antd';
import { ApplicationDefinitionsStore, ProjectsStore } from '../../common/stores';
import { PackageStateResult, ApplicationDefinition } from '../../common/services/types';
import { HomeVisualStore } from '../stores';

type Props = {
    appStore: ApplicationDefinitionsStore;
    projectsStore: ProjectsStore;
    homeStore: HomeVisualStore;
    resetSearch: () => void;
};

export const ApplicationsMenu: React.FC<Props> = ({ appStore, projectsStore, homeStore, resetSearch }) => {
    const analyze = async (pkgIds: string[], appId: string, pkgName: string) => {
        var app = appStore.projectApplications ? appStore.projectApplications.find(a => a.id === appId) : undefined;
        if (!app) {
            return;
        }

        if (pkgIds.length === 1 && projectsStore.selectedPackage) {
            const type = projectsStore.selectedPackage.type;
            await homeStore.analyzePackage(pkgIds[0], appId, pkgName, app.extension, type, app.name);
        } else {
            await homeStore.analyzeMultiplePackages(pkgIds, appId, pkgName, app.extension, app.name);
        }

        resetSearch();
    };

    const groupedApplications = React.useMemo(() => {
        if (!appStore.projectApplications) {
            return {};
        }

        const UNGROUPED_KEY = 'Ungrouped';
        const groups: { [groupName: string]: ApplicationDefinition[] } = {};

        appStore.projectApplications.forEach(app => {
            const groupName = app.group || UNGROUPED_KEY;
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(app);
        });

        Object.keys(groups).forEach(groupName => {
            groups[groupName].sort((a, b) => a.name.localeCompare(b.name));
        });

        return groups;
    }, [appStore.projectApplications]);

    const renderApplicationItem = (app: ApplicationDefinition, isUngrouped: boolean = false) => (
        <Menu.Item
            data-id={
                isUngrouped
                    ? `new-contract-applications-${app.id}`
                    : `new-contract-applications-${app.group || 'ungrouped'}-${app.id}`
            }
            key={app.id}
            onClick={() => analyze(projectsStore.selectedPackageIds, app.id, projectsStore.selectedPackageName)}
            disabled={projectsStore.selectedPackageIds.length > 1 && !app.capabilities?.isBulkProcessing}
        >
            {app.name}
        </Menu.Item>
    );

    const applicationsMenu = (
        <Menu data-id="new-contract-applications">
            {(() => {
                const UNGROUPED_KEY = 'Ungrouped';
                const ungroupedApps = groupedApplications[UNGROUPED_KEY] || [];
                const sortedUngroupedApps = [...ungroupedApps].sort((a, b) => a.name.localeCompare(b.name));
                return sortedUngroupedApps.map(app => renderApplicationItem(app, true));
            })()}

            {Object.entries(groupedApplications)
                .filter(([groupName]) => groupName !== 'Ungrouped')
                .sort(([groupNameA], [groupNameB]) => groupNameA.localeCompare(groupNameB))
                .map(([groupName, apps]) => (
                    <Menu.SubMenu
                        data-id={`new-contract-applications-${groupName.toLowerCase()}`}
                        key={groupName}
                        title={groupName}
                    >
                        {apps.map(app => renderApplicationItem(app, false))}
                    </Menu.SubMenu>
                ))}
        </Menu>
    );

    return (
        <Dropdown
            data-id="new-contract-new-dropdown"
            overlay={applicationsMenu}
            placement="topLeft"
            trigger={['click']}
            disabled={
                !appStore.projectApplications ||
                appStore.projectApplications.length === 0 ||
                projectsStore.selectedPackageState !== PackageStateResult.Ready
            }
        >
            <Button data-id="new-contract-new-dropdown-button" type="primary" loading={appStore.loading}>
                Analyse
            </Button>
        </Dropdown>
    );
};

export default observer(ApplicationsMenu);
